// src/components/address/AddressCard.jsx
import React from 'react';
import Button from '../common/Button';

/**
 * Address Card Component
 * 
 * Used in:
 * - CheckoutPage (select delivery address)
 * - AddressesPage (manage addresses)
 * 
 * Features:
 * - Display address details
 * - Default address badge
 * - Select for checkout
 * - Edit/Delete actions
 */
const AddressCard = ({
  address,
  selectable = false,
  selected = false,
  onSelect,
  onEdit,
  onDelete,
  onSetDefault,
  showActions = true,
}) => {
  return (
    <div
      className={`
        bg-white rounded-lg border-2 p-4 transition-all
        ${selectable ? 'cursor-pointer hover:border-blue-300' : ''}
        ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
      `}
      onClick={selectable ? onSelect : undefined}
    >
      {/* Header with Default Badge */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{address.fullName}</h3>
          <p className="text-sm text-gray-600">{address.phone}</p>
        </div>
        
        {/* Default Badge */}
        {address.isDefault && (
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded">
            Default
          </span>
        )}
        
        {/* Selected Badge */}
        {selected && (
          <span className="bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded">
            Selected
          </span>
        )}
      </div>
      
      {/* Address Lines */}
      <div className="text-sm text-gray-700 space-y-1">
        <p>{address.addressLine1}</p>
        {address.addressLine2 && <p>{address.addressLine2}</p>}
        <p>
          {address.city}, {address.state} - {address.pincode}
        </p>
        <p>{address.country}</p>
      </div>
      
      {/* Actions */}
      {showActions && (
        <div className="mt-4 flex gap-2">
          {!address.isDefault && onSetDefault && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault(address.id);
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Set as Default
            </button>
          )}
          
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(address);
              }}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Edit
            </button>
          )}
          
          {onDelete && !address.isDefault && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(address.id);
              }}
              className="text-sm text-red-600 hover:text-red-700 font-medium ml-auto"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressCard;